import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import IconPlay from '@fortuneo/ui-icons/general/IconPlay'
import IconSchedule from '@fortuneo/ui-icons/general/IconSchedule'
import { withDisplayName } from '@hocs/with-display-name'
import { useMatchMedia } from '@hooks/use-match-media'
import { bold } from '@render-mark/bold'
import { italic } from '@render-mark/italic'
import { underline } from '@render-mark/underline'
import { deprecatedParagraph } from '@render-node/deprecated-paragraph'
import { documentRichMedia } from '@render-node/document-rich-media'
import { hr } from '@render-node/hr'
import { hyperlink } from '@render-node/hyperlink'
import { listItem } from '@render-node/list-item'
import { olList } from '@render-node/ol-list'
import { paragraphNoGutter } from '@render-node/paragraph-no-gutter'
import { quote } from '@render-node/quote'
import { ulList } from '@render-node/ul-list'
import { renderText } from '@render-text/index'
import RichText from '@resolvers/rich-text'
import Button from '@ui/atoms/button'
import { Card } from '@ui/card/card'
import { CardHeader } from '@ui/card-header'
import { Chip } from '@ui/chip'
import MediaPlayer from '@ui/media/media-player'
import { MEDIA_FORMAT } from '@ui/media/unsupported-media-message'
import { ModalUI } from '@ui/modal/modal'
import { device } from '@utils/breakpoints'
import { clsx } from 'clsx'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { graphql } from 'gatsby'
import React, { useCallback, useMemo, useRef, useState } from 'react'

dayjs.extend(duration)

const secondsToHms = (s: number): string => {
  const d = dayjs.duration(s * 1000)
  const hours = d.hours()
  const minutes = d.minutes()

  return `${hours > 0 ? `${hours} h ` : ''}${minutes < 10 ? `0` : ''}${
    minutes === 0 ? '1' : minutes
  }min`
}

const ContentfulWrapperRichMedia: Utils.EntryFC<
  Queries.ContentfulWrapperRichMediaFragment
> = ({
  content: { titleMedia, description, thumbnail, media, category },
  entryInspectorProps,
}) => {
  const isLaptopUp = useMatchMedia(device.laptop)

  const [isVisible, setIsVisible] = useState(false)

  const dialogContentRef = useRef()
  const [isDialogContentScrolled, setIsDialogContentScrolled] = useState(false)

  const onScroll = useCallback(() => {
    if (dialogContentRef.current) {
      const { scrollTop } = dialogContentRef.current
      if (scrollTop !== 0 && !isDialogContentScrolled) {
        setIsDialogContentScrolled(true)
      } else if (scrollTop === 0 && isDialogContentScrolled) {
        setIsDialogContentScrolled(false)
      }
    }
  }, [isDialogContentScrolled])

  const onOpenClick = useCallback(() => {
    setIsVisible(true)
  }, [])
  const onCloseClick = useCallback(() => {
    setIsVisible(false)
  }, [])

  const duration = useMemo(() => {
    if (media?.metadata?.format?.duration) {
      return secondsToHms(media?.metadata?.format?.duration)
    }

    return null
  }, [media])

  const optionsMedia = useMemo(() => {
    const isAudioMedia = media?.file?.contentType === MEDIA_FORMAT.AUDIO
    if (isAudioMedia) {
      return { format: media.file?.contentType, width: '0', height: '0' }
    }

    if (media) {
      return {
        format: media.file?.contentType,
        width: '100%',
        height: 'auto',
      }
    }

    return {}
  }, [media])

  return (
    <>
      <Card {...entryInspectorProps()}>
        <CardHeader asset={thumbnail} />
        <div className="relative flex flex-col justify-between p-10">
          {category && (
            <Chip
              className="absolute top-0 translate-y-[-50%]"
              label={category}
            />
          )}
          <div>
            {titleMedia && (
              <h3 className="mb-4 text-balance text-165">{titleMedia}</h3>
            )}
            {description && (
              <div className="mb-8">
                <RichText
                  content={description}
                  options={{
                    renderMark: {
                      [MARKS.UNDERLINE]: underline,
                      [MARKS.ITALIC]: italic,
                      [MARKS.BOLD]: bold,
                    },
                    renderNode: {
                      [BLOCKS.DOCUMENT]: documentRichMedia,
                      [BLOCKS.PARAGRAPH]: paragraphNoGutter,
                      [BLOCKS.QUOTE]: quote,
                      [BLOCKS.OL_LIST]: olList,
                      [BLOCKS.UL_LIST]: ulList,
                      [BLOCKS.LIST_ITEM]: listItem,
                      [BLOCKS.HR]: hr,
                      [INLINES.HYPERLINK]: hyperlink,
                    },
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            {duration && (
              <div className="mr-[5px] flex items-center">
                <IconSchedule className="mr-1" />
                <span className="text-085">{duration}</span>
              </div>
            )}
            <Button
              className="!min-h-[40px] !px-8 !py-0"
              disabled={!media}
              icon={IconPlay}
              variant="primary"
              onClick={onOpenClick}
            >
              {media ? ' Lecture' : 'Bientôt disponible'}
            </Button>
          </div>
        </div>
      </Card>
      <ModalUI
        fullScreen={!isLaptopUp}
        visible={isVisible}
        onClose={onCloseClick}
      >
        <div
          className={clsx(
            isDialogContentScrolled
              ? 'shadow-[1px_6px_5px_1px_rgba(35,35,35,6%)]'
              : ''
          )}
          style={{
            '& > audio': {
              width: '100%',
            },
          }}
        >
          {media?.file?.url && (
            <MediaPlayer
              options={optionsMedia}
              preview={<CardHeader asset={thumbnail} />}
              url={media.file.url}
            />
          )}
        </div>
        <div
          ref={dialogContentRef}
          className="mt-5 max-h-[300px] overflow-x-hidden overflow-y-scroll"
          onScroll={onScroll}
        >
          {titleMedia && (
            <h3 className="mb-4 text-balance text-165">{titleMedia}</h3>
          )}
          {description && (
            <RichText
              content={description}
              options={{
                renderMark: {
                  [MARKS.UNDERLINE]: underline,
                  [MARKS.ITALIC]: italic,
                  [MARKS.BOLD]: bold,
                },
                renderNode: {
                  [BLOCKS.QUOTE]: quote,
                  [BLOCKS.OL_LIST]: olList,
                  [BLOCKS.UL_LIST]: ulList,
                  [BLOCKS.LIST_ITEM]: listItem,
                  [BLOCKS.HR]: hr,
                  [BLOCKS.PARAGRAPH]: deprecatedParagraph,
                },
                renderText,
              }}
            />
          )}
        </div>
      </ModalUI>
    </>
  )
}

export const query = graphql`
  fragment ContentfulWrapperRichMedia on ContentfulWrapperRichMedia {
    ...ContentfulEntry
    titleMedia: title
    description {
      rich
    }
    thumbnail {
      ...ContentfulWrapperRichMediaThumbnail
    }
    media {
      ...ContentfulDefaultAsset
      metadata: getMetadata {
        ...mediaMetadata
      }
    }
    category
  }

  fragment mediaMetadata on MediaMetadata {
    format {
      duration
    }
  }
`

export default withDisplayName(
  ContentfulWrapperRichMedia,
  'ContentfulWrapperRichMedia'
)
